/**
 * MenuItemCard — displays a single menu item in the BOH grid.
 * Includes feature toggle for "Special of the Day".
 */
import { Edit, Trash2, Star, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { API_URL } from '@/config/api';

const TYPE_BADGE = {
  canteen: 'bg-blue-600',
  office_platters: 'bg-purple-600',
};

const TYPE_LABEL = {
  daily_meals: 'Daily Meal',
  canteen: 'Canteen Item',
  office_platters: 'Office Platter',
};

export default function MenuItemCard({ item, onEdit, onDelete, onUpdate }) {
  const { token } = useAuth();
  const [toggling, setToggling] = useState(false);
  const badgeColor = TYPE_BADGE[item.menu_type] || 'bg-primary';

  const toggleFeature = async (e) => {
    e.stopPropagation();
    setToggling(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/menu/${item.item_id}/feature`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      toast.success(d.message);
      if (onUpdate) onUpdate();
    } catch (e) { toast.error(e.message); }
    finally { setToggling(false); }
  };

  return (
    <Card className="overflow-hidden" data-testid={`menu-item-${item.item_id}`}>
      <div className="aspect-video relative">
        <img
          src={item.image_url || 'https://images.unsplash.com/photo-1671612451404-f4f8fc5fe25e?w=400'}
          alt={item.name}
          className="w-full h-full object-cover"
        />
        {!item.is_available && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <span className="text-white font-semibold">Unavailable</span>
          </div>
        )}
        <Badge className={`absolute top-2 right-2 text-xs ${badgeColor}`}>
          {TYPE_LABEL[item.menu_type] || 'Daily Meal'}
        </Badge>
        {item.is_featured && (
          <Badge className="absolute top-2 left-2 text-xs bg-amber-500 text-white">
            <Star className="h-3 w-3 mr-0.5 fill-white" /> Featured
          </Badge>
        )}
      </div>

      <CardContent className="p-4">
        <div className="flex justify-between items-start mb-2">
          <div>
            <h3 className="font-semibold text-secondary-900">{item.name}</h3>
            <p className="text-xs text-muted-foreground">{item.category}</p>
          </div>
          <span className="font-bold text-primary">R{item.price.toFixed(2)}</span>
        </div>

        <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{item.description}</p>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Stock: {item.stock_quantity ?? '∞'}</span>
            {item.sides_config?.has_sides && (
              <Badge variant="outline" className="text-xs">
                {item.sides_config.free_sides_count > 0
                  ? `${item.sides_config.free_sides_count} side${item.sides_config.free_sides_count !== 1 ? 's' : ''} included`
                  : 'Sides optional (paid)'}
              </Badge>
            )}
          </div>
          <div className="flex gap-1.5">
            {/* Feature toggle */}
            {item.menu_type === 'daily_meals' && (
              <Button
                variant={item.is_featured ? "default" : "outline"}
                size="sm"
                className={item.is_featured ? "bg-amber-500 hover:bg-amber-600 text-white h-8 px-2" : "h-8 px-2 text-amber-600 hover:bg-amber-50"}
                onClick={toggleFeature}
                disabled={toggling}
                title={item.is_featured ? "Remove from Specials" : "Feature as Special"}
                data-testid={`feature-${item.item_id}`}
              >
                {toggling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Star className={`h-3.5 w-3.5 ${item.is_featured ? 'fill-white' : ''}`} />}
              </Button>
            )}
            <Button variant="outline" size="sm" className="h-8 px-2" onClick={() => onEdit(item)} data-testid={`edit-${item.item_id}`}>
              <Edit className="h-3.5 w-3.5" />
            </Button>
            <Button variant="outline" size="sm" className="h-8 px-2 text-destructive hover:bg-destructive hover:text-white" onClick={() => onDelete(item.item_id)} data-testid={`delete-${item.item_id}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
